import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUp, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { ContactButton, Wordmark } from './BrandElements';
import { ThemeToggle } from './ThemeToggle';

const navigation = [
  { id: 'approach', label: 'What we’re building' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'founder', label: 'Founder story' },
];

export const ScrollManager = () => {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    document.title = pathname.replace(/\/$/, '') === '/brand' ? '3tched — Brand reference' : '3tched — Systems that govern themselves as they operate.';
    const frame = requestAnimationFrame(() => {
      if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, key]);
  return null;
};

export const SiteHeader = ({ theme, onToggleTheme }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');
  const location = useLocation();
  const toggle = useRef(null);
  useEffect(() => { setOpen(false); setActive(''); }, [location.pathname, location.hash]);
  useEffect(() => {
    if (location.pathname !== '/') return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) setActive(entry.target.id); });
    }, { rootMargin: '-15% 0px -60% 0px' });
    document.querySelectorAll('main section[id]').forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const escape = event => { if (event.key === 'Escape') { setOpen(false); toggle.current?.focus(); } };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [open]);
  const links = prefix => navigation.map(item => (
    <Link to={`/#${item.id}`} key={item.id} onClick={() => setOpen(false)} className={active === item.id ? 'nav-link active' : 'nav-link'} aria-current={active === item.id ? 'location' : undefined} data-testid={`${prefix}-nav-${item.id}`}>{item.label}</Link>
  ));
  return (
    <header className="site-header" id="top" data-testid="site-header">
      <div className="container header-inner">
        <Link to="/" className="home-link" aria-label="3tched home" data-testid="header-home-link"><Wordmark id="header" /></Link>
        <nav className="desktop-nav" aria-label="Main navigation">{links('desktop')}</nav>
        <ThemeToggle theme={theme} onToggle={onToggleTheme} />
        <ContactButton outline id="header-contact-button" className="header-contact" label="Talk with us" />
        <Button ref={toggle} variant="ghost" size="icon" className="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="mobile-navigation" aria-label={open ? 'Close navigation' : 'Open navigation'} data-testid="mobile-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</Button>
      </div>
      {open && <nav id="mobile-navigation" className="mobile-navigation" aria-label="Mobile navigation" data-testid="mobile-navigation">{links('mobile')}<ContactButton id="mobile-contact-button" label="Talk with us" /></nav>}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div className="container footer-main">
      <Link to="/" className="home-link" aria-label="3tched home" data-testid="footer-home-link"><Wordmark id="footer" /></Link>
      <p className="footer-statement" data-testid="footer-statement">A single source of truth.<br />Defended on purpose.</p>
      <div className="footer-links"><Link to="/brand" className="text-link" data-testid="brand-resources-link">Brand resources</Link><button className="text-link" onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })} data-testid="back-to-top-button">Back to top <ArrowUp size={14} aria-hidden="true" /></button></div>
    </div>
    <div className="container footer-bottom"><span data-testid="copyright">© {new Date().getFullYear()} 3tched</span><span className="mono" data-testid="footer-brand-line">CONTROL & ACCOUNTABILITY, CONNECTED.</span></div>
  </footer>
);